import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { useApi } from "../utils/Api";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function Contact() {
  const { apiFetch } = useApi();
  const hours = useMemo(
    () => [
      { day: "Monday", time: "9am - 6pm" },
      { day: "Tuesday", time: "9am - 6pm" },
      { day: "Wednesday", time: "9am - 6pm" },
      { day: "Thursday", time: "9am - 6pm" },
      { day: "Friday", time: "9am - 6pm" },
      { day: "Saturday", time: "9am - 6pm" },
      { day: "Sunday", time: "Closed" },
    ],
    [],
  );

  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ success: false, message: "" });

  const onChange = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setStatus({ success: false, message: "" });

      const res = await apiFetch("submit_contact", form);
      if (res.success) {
        setStatus({ success: true, message: res.message });
        setForm({ name: "", email: "", message: "" });
      } else {
        setStatus({ success: false, message: res.message || "Failed to send message." });
      }
    } catch (err) {
      setStatus({ success: false, message: "An error occurred. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  const address =
    "Unit 2, Beechwood House, Milnrow Road, Rochdale, Manchester OL16 2AA";
  const phone = "+44 7486 160965";
  const email = "info@kinewsstudiolimited.co.uk";

  const mapSrc =
    "https://www.google.com/maps?q=Unit%202,%20Beechwood%20House,%20Milnrow%20Road,%20Rochdale,%20Manchester%20OL16%202AA&output=embed";

  return (
    <>
      <SEO
        title="Contact Kinews Studio Limited"
        description="Get in touch with Kinews Studio Limited for bookings, events, studio rentals and enquiries. We are here to help you capture your moments."
        url={window.location.href}
      />
      <main className="min-h-screen bg-white text-black">
        {/* HERO */}
        <section className="relative overflow-hidden bg-[#050505] text-white">
          {/* Lighting Effects */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden">
            <div className="absolute -left-[10%] -top-[10%] h-[40rem] w-[40rem] rounded-full bg-pink-500/15 blur-[120px]" />
            <div className="absolute -right-[10%] top-[20%] h-[35rem] w-[35rem] rounded-full bg-pink-600/10 blur-[100px]" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[50rem] w-[50rem] rounded-full bg-pink-500/5 blur-[150px]" />
          </div>

          <div className="relative mx-auto max-w-7xl px-5 pb-14 pt-28 md:pb-24 md:pt-40">
            <h1 className="mt-3 text-4xl font-bold tracking-tighter md:text-5xl lg:text-6xl">
              Contact
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60 md:text-xl">
              Get in touch for bookings, questions, or custom requests.
            </p>
          </div>
        </section>

        {/* MAIN */}
        <section className="bg-white">
          <div className="mx-auto max-w-7xl px-5 py-14">
            <div className="grid gap-8 lg:grid-cols-12">
              {/* LEFT */}
              <div className="lg:col-span-5 space-y-4">
                <div className="rounded-3xl border border-black/10 bg-white p-6">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                    Studio
                  </p>
                  <h2 className="mt-2 text-xl font-semibold">Get in touch</h2>

                  <ul className="mt-4 space-y-3 text-sm text-black/70">
                    <li>
                      <span className="font-semibold text-black">Address:</span>{" "}
                      {address}
                    </li>

                    <li>
                      <span className="font-semibold text-black">Phone:</span>{" "}
                      <a
                        className="underline underline-offset-4 hover:text-pink-500"
                        href="tel:+447486160965"
                      >
                        {phone}
                      </a>
                    </li>

                    <li>
                      <span className="font-semibold text-black">
                        Landline:
                      </span>{" "}
                      <a
                        className="underline underline-offset-4 hover:text-pink-500"
                        href="tel:01706 803 538"
                      >
                        01706 803 538
                      </a>
                    </li>

                    <li>
                      <span className="font-semibold text-black">Email:</span>{" "}
                      <a
                        className="underline underline-offset-4 hover:text-pink-500"
                        href={`mailto:${email}`}
                      >
                        {email}
                      </a>
                    </li>
                  </ul>

                  {/* SOCIAL */}
                  <div className="mt-6">
                    <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                      Social
                    </p>

                    <div className="mt-3 flex flex-wrap gap-3">
                      {[
                        "Instagram",
                        "Facebook",
                        "TikTok",
                        "YouTube",
                        "WhatsApp",
                      ].map((s) => (
                        <span
                          key={s}
                          className="rounded-2xl border border-black/10 px-4 py-2 text-sm hover:border-pink-500 hover:text-pink-500 transition cursor-pointer"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* HOURS */}
                <div className="rounded-3xl border border-black/10 bg-neutral-50 p-6">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                    Hours
                  </p>

                  <h3 className="mt-2 text-lg font-semibold">Opening Hours</h3>

                  <ul className="mt-4 space-y-2 text-sm text-black/70">
                    {hours.map((h) => (
                      <li key={h.day} className="flex justify-between">
                        <span className="font-medium">{h.day}</span>
                        <span className="text-black/60">{h.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* RIGHT */}
              <div className="lg:col-span-7 space-y-4">
                {/* FORM */}
                <div className="rounded-3xl border border-black/10 bg-white p-6 md:p-8">
                  <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                    Message
                  </p>

                  <h2 className="mt-2 text-xl font-semibold">Send a message</h2>

                  {status.success ? (
                    <div className="mt-6 rounded-2xl bg-green-50 p-8 text-center border border-green-100">
                      <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                        <CheckCircle2 size={24} />
                      </div>
                      <h3 className="text-lg font-bold text-green-900 mb-2">Message Sent!</h3>
                      <p className="text-green-700 text-sm mb-6">{status.message}</p>
                      <button 
                        onClick={() => setStatus({ success: false, message: "" })}
                        className="text-sm font-bold text-green-900 underline underline-offset-4"
                      >
                        Send another message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={onSubmit} className="mt-6 space-y-4">
                      <div className="grid gap-4 md:grid-cols-2">
                        <input
                          className="w-full rounded-2xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-pink-500 transition-all"
                          placeholder="Full name"
                          value={form.name}
                          onChange={onChange("name")}
                          required
                        />

                        <input
                          className="w-full rounded-2xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-pink-500 transition-all"
                          placeholder="Email"
                          type="email"
                          value={form.email}
                          onChange={onChange("email")}
                          required
                        />
                      </div>

                      <textarea
                        className="w-full rounded-2xl border border-black/15 px-4 py-3 text-sm outline-none focus:border-pink-500 transition-all resize-none"
                        rows={6}
                        placeholder="How can we help?"
                        value={form.message}
                        onChange={onChange("message")}
                        required
                      />

                      {status.message && !status.success && (
                        <p className="text-sm text-red-500">{status.message}</p>
                      )}

                      <button
                        type="submit"
                        disabled={submitting}
                        className="inline-flex items-center gap-3 rounded-2xl bg-pink-500 px-8 py-3 text-sm font-semibold text-white hover:bg-pink-600 transition disabled:bg-pink-500/40"
                      >
                        {submitting ? (
                          <>
                            <Loader2 size={18} className="animate-spin" />
                            Sending...
                          </>
                        ) : (
                          "Send message"
                        )}
                      </button>

                      <p className="text-xs text-black/50">
                        We typically reply within 24 hours.
                      </p>
                    </form>
                  )}
                </div>

                {/* MAP */}
                <div className="rounded-3xl border border-black/10 overflow-hidden">
                  <div className="px-6 py-5">
                    <p className="text-[11px] tracking-[0.2em] uppercase text-pink-500/80">
                      Location
                    </p>
                    <h3 className="mt-2 text-lg font-semibold">Find us</h3>
                  </div>

                  <div className="h-[360px] w-full">
                    <iframe
                      src={mapSrc}
                      className="h-full w-full"
                      loading="lazy"
                      title="Google Maps location of Kinews Studio"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-black text-white">
          <div className="mx-auto max-w-7xl px-5 py-14">
            <div className="rounded-3xl border border-pink-500/30 bg-white/5 p-8 md:flex md:justify-between">
              <div>
                <h2 className="text-2xl font-semibold">Ready to book?</h2>
                <p className="mt-2 text-sm text-white/70">
                  Pick a session and secure a slot.
                </p>
              </div>

              <Link
                to="/book"
                className="inline-flex items-center justify-center leading-none rounded-2xl bg-pink-500 px-6 py-3 text-sm font-semibold text-white hover:bg-pink-600 transition"
              >
                Go to Bookings
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
